<template>
  <div class="custom-col-size flex-grow-1">
    <nav
      class="sports row text-center align-items-center justify-content-between justify-content-lg-center"
      aria-label="Sports Navigation"
    >
      <router-link
        v-for="sport in filteredSports"
        :key="sport.id"
        :to="sport.link"
        :class="[
          'px-2 py-2 px-lg-3',
          'sport mr-1 mr-lg-2',
          { 'selected': isSportSelected(sport) },
        ]"
        :disabled="sport.disabled"
      >
        <div class="sport-name mb-lg-2">
          {{ sport.name }}
        </div>
        <template v-if="sport.sport === 'custom'">
          <font-awesome-icon
            :icon="getFaSportIcon(sport)"
            class="sport-logo-awesome fi miscellaneous"
            :flip="sport.sport === 'fighting' ? 'horizontal' : null"
            @click="refreshData(sport)"
          />
        </template>
        <template v-else>
          <img
              :src="`/icons/${sport.iconSlug ?? sport.name.toLowerCase()}.svg`"
              class="sport-logo fi"
              :class="sport.icon"
            />
          <!-- <i
            :class="sport.icon"
            class="sport-logo fi"
            @click="refreshData(sport)"
          /> -->
        </template>
      </router-link>
    </nav>
  </div>
</template>

<script setup>
// core
import { ref, computed, watch } from 'vue';
import { useUserStore } from '@/stores/user';
import { useExchangeStore } from '@/stores/exchange';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { emitter } from '@/core/emitter';
// icons
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { getFaSportIcon } from '@/core/getIcon.js';
import {
  allPropsRouteNames,
  adjustLinksForExchange,
  baseSports,
  filterAvailableSports,
} from '@/core/sportsNav.js';

// init user store
const userStore = useUserStore();
const { user } = storeToRefs(userStore);

const exchangeStore = useExchangeStore();

const route = useRoute();
const sportSelected = ref(null);

const filteredSports = computed(() => {
  const adjustedSports = userStore.user.sportsbookDefault
    ? baseSports
    : baseSports.map(adjustLinksForExchange);
  return filterAvailableSports(
    adjustedSports,
    exchangeStore.sportsNavigations.sportsAvailable,
    { excludeCustom: true },
  );
});

function isSportSelected(sport) {
  if (sport.id === 'all-props') {
    return allPropsRouteNames.includes(route.name);
  }
  return (
    sportSelected.value === sport.name.toLowerCase() &&
    route.params.sport === sport.name.toLowerCase()
  );
}

function refreshData(sport) {
  const routeLeague = route.params.league;
  if (
    (sport.sport === route.params.sport &&
      routeLeague &&
      sport.name.toUpperCase() === routeLeague.toUpperCase()) ||
    (sport.sport === 'soccer' &&
      sport.sport === route.params.sport &&
      routeLeague === 'all')
  ) {
    exchangeStore.getSportsData(sport.sport);
    emitter.emit('disableError', { error: false });
  }
}

watch(
  () => route.params,
  async (newParams) => {
    if (
      newParams.sport &&
      Object.keys(exchangeStore.sportsNavigations).length
    ) {
      sportSelected.value = newParams.sport;
      exchangeStore.leagues =
        exchangeStore.sportsNavigations.sportsAvailable[newParams.sport];
    }
  },
);
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/theme/variables.scss' as theme;

.sports {
  transform: scale(1);
  display: block;
  white-space: nowrap;
  overflow-y: hidden;

  .miscellaneous {
    transform: scale(0.85);
  }

  .sport {
    transition: color ease 0.2s;
    color: white;
    display: inline-block;

    &:hover,
    &.router-link-active {
      color: white;
      text-decoration: none;
      transform: scale(1.025);
      transition: transform 0.3s;
      background-color: #5B6EF533;
      border-radius: 11px;
      box-shadow: 0px 0px 10px 0px #0000001A;
    }

    &--disabled {
      cursor: default;

      .no-events {
        opacity: 1;
      }

      &:hover {
        color: #969ccb;
        text-decoration: none;
      }
    }

    &-name {
      font-weight: theme.$font-weight-normal;
      font-size: theme.$font-size-sm;
      line-height: 1;
      vertical-align: middle;

      @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
        font-size: 0.7rem;
      }

      @media screen and (max-width: 370px) {
        font-size: 0.6rem;
        margin-bottom: 5px;
      }

      .no-events {
        opacity: 0;
        font-size: theme.$font-size-xs;
        font-weight: lighter;
      }
    }

    &-logo {
      font-size: 44px;
      line-height: 0;
    }

    &-logo-awesome {
      font-size: 38px;
      line-height: 0;
      margin-bottom: 5px;
    }
  }

  @media screen and (max-width: map.get(theme.$grid-breakpoints, lg)) {
    overflow-x: auto;
    overflow-y: hidden;
    flex-wrap: nowrap;

    .sport {
      min-width: 76px;

      @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
        min-width: fit-content;
        transform: scale(0.9);
        transform-origin: center;
      }
    }
  }

  .sport {
    &-logo {
      @media screen and (max-width: 840px) {
        font-size: 3rem;
      }

      @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
        font-size: 2.5rem;
      }

      @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
        font-size: 2.25rem;
      }

      @media screen and (max-width: 370px) {
        font-size: 1.75rem;
      }
    }
  }
}

.custom-col-size {
  flex: 0 0 65.66667%;
  max-width: 65.66667%;

  @media screen and (max-width: map.get(theme.$grid-breakpoints, xl)) {
    flex: 0 0 58.66667%;
    max-width: 58.66667%;
  }
  @media screen and (max-width: map.get(theme.$grid-breakpoints, lg)) {
    flex: 0 0 60.66667%;
    max-width: 60.66667%;
  }
  @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
    max-width: 57.66667%;
    padding-right: 15px;
  }
  @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
    flex: 1 1 0;
    min-width: 0;
    max-width: none;
    padding-left: 8px;
    padding-right: 8px;
  }
}

.selected {
  color: white;
  text-decoration: none;
  transform: scale(1.025);
  transition: transform 0.3s;
  background-color: #5B6EF533;
  border-radius: 11px;
  box-shadow: 0px 0px 10px 0px #0000001A;
}
</style>
